import React from "react";
import { motion } from "framer-motion";
import { Cpu, Cloud, CheckCircle2, Code2 } from "lucide-react";
import { STATS, CONTACT } from "../data/portfolioData";

export default function About() {
  return (
    <section id="about" className="py-20 relative overflow-hidden bg-white border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-900 text-xs font-mono font-bold shadow-sm"
          >
            <Code2 size={15} /> ABOUT ME & ARCHITECTURAL SUMMARY
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900"
          >
            Full Stack Engineering & <span className="gradient-text-indigo">Cloud Operations</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-slate-800 text-base sm:text-lg font-medium leading-relaxed"
          >
            {CONTACT.bio}
          </motion.p>
        </div>

        {/* Statistics Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-14 grid grid-cols-2 md:grid-cols-5 gap-4"
        >
          {STATS.map((stat) => (
            <div
              key={stat.label}
              className="p-6 rounded-2xl bg-slate-50 border border-slate-200 text-center space-y-1 hover:border-indigo-500 transition-all duration-300 hover:scale-[1.03] group shadow-sm"
            >
              <div className="text-3xl sm:text-4xl font-black text-slate-900">
                <span className="gradient-text-indigo">
                  {stat.value}
                </span>
                <span className="text-indigo-700 text-xl font-bold">{stat.suffix}</span>
              </div>
              <div className="text-xs font-mono font-bold text-slate-700">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Value Pillars */}
        <div className="mt-16 grid md:grid-cols-3 gap-8">
          {[
            {
              icon: Code2,
              title: "Full Stack Software Engineering",
              desc: "Building dynamic user interfaces with React.js and backend API microservices with Python (Django) & Drupal CMS with PostgreSQL & MySQL databases.",
              highlights: ["React.js SPA Development", "Python Django REST APIs", "Drupal Module Customization"],
            },
            {
              icon: Cloud,
              title: "Cloud Infrastructure & IaC",
              desc: "Automating cloud infrastructure provisioning across AWS & Azure using modular Terraform templates and Ansible configuration management.",
              highlights: ["AWS & Azure Cloud Services", "Modular Infrastructure as Code", "Ansible Automation Playbooks"],
            },
            {
              icon: Cpu,
              title: "DevOps & Containerization",
              desc: "Containerizing microservices with multi-stage Docker builds, orchestrating workloads on Kubernetes (EKS), and syncing with GitOps ArgoCD.",
              highlights: ["Kubernetes Cluster Management", "Docker Multi-stage Builds", "GitOps ArgoCD Pipelines"],
            },
          ].map((pillar, i) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              className="p-8 rounded-3xl bg-slate-50 border border-slate-200 hover:border-indigo-500 transition-all duration-300 hover:-translate-y-1.5 shadow-sm hover:shadow-md flex flex-col justify-between group"
            >
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-indigo-100 border border-indigo-200 flex items-center justify-center text-indigo-700 group-hover:scale-110 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300">
                  <pillar.icon size={24} />
                </div>
                <h3 className="text-xl font-bold text-slate-900">
                  {pillar.title}
                </h3>
                <p className="text-sm text-slate-700 font-normal leading-relaxed">
                  {pillar.desc}
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200 space-y-2">
                {pillar.highlights.map((h) => (
                  <div key={h} className="flex items-center gap-2 text-xs font-mono font-bold text-slate-800">
                    <CheckCircle2 size={15} className="text-emerald-600 shrink-0" />
                    <span>{h}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
